/**
 * /////////////////////////////////////////////////////////////////////////////
 *
 * https://keeptrack.space
 *
 * @Copyright (C) 2025 Kruczek Labs LLC
 *
 * KeepTrack is free software: you can redistribute it and/or modify it under the
 * terms of the GNU Affero General Public License as published by the Free Software
 * Foundation, either version 3 of the License, or (at your option) any later version.
 *
 * KeepTrack is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License along with
 * KeepTrack. If not, see <http://www.gnu.org/licenses/>.
 *
 * /////////////////////////////////////////////////////////////////////////////
 */
import { SolarBody } from '@app/engine/core/interfaces';
import { SpaceObjectType } from '@ootk/src/main';
import { settingsManager } from '../../../../settings/settings';
import { CelestialBody, PlanetColors } from './celestial-body';
import { SaturnRings } from './saturn-rings';
export var SaturnTextureQuality;
(function (SaturnTextureQuality) {
    SaturnTextureQuality["POTATO"] = "512";
    SaturnTextureQuality["HIGH"] = "2k";
    SaturnTextureQuality["ULTRA"] = "4k";
})(SaturnTextureQuality || (SaturnTextureQuality = {}));
export class Saturn extends CelestialBody {
    radius = 69911;
    RADIUS = this.radius;
    NUM_HEIGHT_SEGS = 64;
    NUM_WIDTH_SEGS = 64;
    orbitalPeriod = 29.4571 * 365.25 * 24 * 3600;
    meanDistanceToSun = 1426666422;
    eci;
    type = SpaceObjectType.GAS_GIANT;
    rings_;
    color = PlanetColors.SATURN;
    textureQuality = SaturnTextureQuality.POTATO;
    constructor() {
        super();
        this.rings_ = new SaturnRings(this);
    }
    async init(gl) {
        await super.init(gl);
        await this.rings_.init(gl);
    }
    getTexturePath() {
        return `${settingsManager.installDirectory}textures/saturn${this.textureQuality}.jpg`;
    }
    useHighestQualityTexture() {
        this.textureQuality = SaturnTextureQuality.ULTRA;
        this.loadTexture();
    }
    getName() {
        return SolarBody.Saturn;
    }
    update(simTime) {
        super.update(simTime);
        this.rings_.update(simTime);
    }
    draw(sunPosition, tgtBuffer = null) {
        if (!this.isLoaded_ || settingsManager.isDisablePlanets) {
            return;
        }
        super.draw(sunPosition, tgtBuffer);
        this.rings_.draw(sunPosition, tgtBuffer);
    }
}
